export const REWARD_REDEMPTION_LOCATIONS_URL = 'treatRedemptionComparison';
export const MOST_POPULAR_REWARDS_URL = 'mostPopularTreats';
export const REWARD_STATISTICS_URL = 'treatStats';
export const REWARD_CAMPAIGNS_TILE_ID = 'topCampaignsByRedemptionRate';

export function isRewardCampaignsTile(tile): boolean {
  const title = `${tile?.title ?? ''}`.trim().toLowerCase();
  return tile?.id === REWARD_CAMPAIGNS_TILE_ID || title === 'reward campaigns';
}

const REWARD_METRICS_TILE_URLS = new Set([
  REWARD_REDEMPTION_LOCATIONS_URL,
  MOST_POPULAR_REWARDS_URL,
  REWARD_STATISTICS_URL
]);

export interface RewardMetricsLocations {
  overallRedemptionRate?: number;
  sameLocationRedemptionRate?: number;
  otherLocationRedemptionRate?: number;
}

export interface RewardMetricsPopularReward {
  name?: string | null;
  title?: string | null;
  rewardName?: string | null;
  redemptionRate?: number;
}

export interface RewardMetricsStatistics {
  offersSent?: number;
  offersUsed?: number;
  offerRedemptionAllCustomers?: number;
}

export interface RewardMetricsAnalytics {
  redemptionLocations?: RewardMetricsLocations;
  mostPopularRewards?: RewardMetricsPopularReward[];
  rewardStatistics?: RewardMetricsStatistics;
}

export function isRewardMetricsTileUrl(url?: string): boolean {
  return !!url && REWARD_METRICS_TILE_URLS.has(url);
}

export function rewardDisplayName(reward: RewardMetricsPopularReward = {}): string {
  return reward.rewardName || reward.title || reward.name || 'Unknown Reward';
}

/**
 * Shapes the resource-route payload into the field names the existing tile
 * sections already read (treatRedemptionComparison, mostPopularTreats, treatStats).
 */
export function mapRewardMetricsTile(url: string, analytics: RewardMetricsAnalytics = {}) {
  if (url === REWARD_REDEMPTION_LOCATIONS_URL) {
    const locations = analytics.redemptionLocations ?? {};
    return {
      totalRedemption: locations.overallRedemptionRate ?? 0,
      redemptionAtMyRestaurant: locations.sameLocationRedemptionRate ?? 0,
      redemptionAtOtherRestaurants: locations.otherLocationRedemptionRate ?? 0
    };
  }

  if (url === REWARD_STATISTICS_URL) {
    const stats = analytics.rewardStatistics ?? {};
    return {
      offersSent: stats.offersSent ?? 0,
      offersUsed: stats.offersUsed ?? 0,
      offerRedemptionAllCustomers: stats.offerRedemptionAllCustomers ?? 0
    };
  }

  if (url === MOST_POPULAR_REWARDS_URL) {
    const topTreatOffers = (analytics.mostPopularRewards ?? []).map((reward) => ({
      productName: rewardDisplayName(reward),
      totalSales: reward.redemptionRate ?? 0
    }));
    return {
      topTreatOffers,
      checkZeroCountData: topTreatOffers.length > 0
    };
  }

  return null;
}

export function isUniqueCustomersSection(section): boolean {
  const prop = `${section?.prop ?? ''}`.toLowerCase();
  const title = `${section?.title ?? ''}`.trim().toLowerCase();
  return prop === 'uniquecustomers' || title === 'unique customers';
}

export function withoutUniqueCustomerSections(tile) {
  if (!tile || !Array.isArray(tile.sections)) {
    return tile;
  }

  const sections = tile.sections
    .map((group) => Array.isArray(group)
      ? group.filter((section) => !isUniqueCustomersSection(section))
      : group)
    .filter((group) => !Array.isArray(group) || group.length > 0);

  return { ...tile, sections };
}
